import { useEffect, useRef } from "react";

import { VariantProps } from "class-variance-authority";
import { Loader } from "lucide-react";
import toast from "react-hot-toast";
import { FormState, UseFormReturn } from "react-hook-form";

import { Button as BaseButton, Button, buttonVariants } from "@/components/ui/button";
import useImagesStore from "@/lib/stores/images";
import { cn, logFrontendError } from "@/lib/utils";

interface FormButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  children: React.ReactNode;
  formState: FormState<any>;
  className?: string;
  type?: "submit" | "reset" | "button";
}

const FormButton = ({ children, formState, className, type = "submit", ...props }: FormButtonProps) => {
  const imageIsUploading = useImagesStore((state) => state.isUploading);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!formState) {
    const error = new Error("FormButton must be used within a form & passed formState");
    logFrontendError("FormButton error:", error);
    throw error;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to submit the form
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();

        // Only trigger if the button is not disabled and is a submit button
        if (type === "submit" && !formState.isSubmitting && !props.disabled && !imageIsUploading && buttonRef.current) {
          buttonRef.current.click();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [formState.isSubmitting, props.disabled, imageIsUploading, type]);

  return (
    <BaseButton
      {...props}
      ref={buttonRef}
      type={type}
      disabled={formState.isSubmitting || props.disabled || imageIsUploading}
      className={cn("relative", className)}
    >
      {formState.isSubmitting || imageIsUploading ? (
        <>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <Loader className={`w-5 animate-spin`} />
          </span>
          <span className="invisible">{children}</span>
        </>
      ) : (
        children
      )}
    </BaseButton>
  );
};

export default FormButton;

export const FormButtonWrapper = ({ form }: { form: UseFormReturn<any> }) => {
  const hasErrors = Object.keys(form.formState.errors).length > 0;
  const prevHasErrors = useRef(false);

  useEffect(() => {
    if (hasErrors && !prevHasErrors.current) {
      logFrontendError(
        "Form validation errors blocking save",
        new Error(JSON.stringify(form.formState.errors, (key, value) => (key === "ref" ? undefined : value)))
      );
      toast.error("Please fix the errors before saving");
    }
    prevHasErrors.current = hasErrors;
  }, [hasErrors, form.formState.errors]);

  return (
    <div
      className={cn(
        `absolute transition duration-500 bottom-0 inset-x-0 border-t p-4 bg-ash-100 flex justify-between z-20`,
        form.formState.isDirty ? "translate-y-0" : "translate-y-full"
      )}
    >
      <Button type="button" variant="outline" onClick={() => form.reset()}>
        Discard Changes
      </Button>
      <FormButton formState={form.formState}>Save</FormButton>
    </div>
  );
};
